/*
  "Add to cart", anywhere on the shop, opens our cart from the right.

  What used to happen: the theme submitted the form, Fastrr's Smart Cart
  noticed and slid its own "Shopping Bag" in — a different cart, with
  different wording, different sums and a different way out. Fastrr now
  loads only on the old /cart page (layout/theme.liquid), so nothing is
  racing us for the click; this takes the add itself so the page never
  reloads, and shows our cart in a panel.

  The cart inside that panel is not a copy. It is assets/sp-cart.js, the
  same script the cart page runs, drawing into the same `[data-sp-cart]`
  box and wearing the same assets/sp-cart.css — loaded the first time
  somebody actually adds something, so a visitor who only browses never
  downloads any of it.
*/
(function () {
  'use strict';

  var drawer = document.querySelector('[data-sp-drawer]');
  if (!drawer) return;

  var me = document.currentScript || document.querySelector('script[data-cart-js]');
  var CART_JS = me && me.getAttribute('data-cart-js');
  var CART_CSS = me && me.getAttribute('data-cart-css');
  var CONFETTI_JS = me && me.getAttribute('data-confetti-js');

  var panel = drawer.querySelector('.spd__panel');
  var loading = false;
  var loaded = false;

  /* ---------- bringing the cart in ---------- */

  function addOnce(tag, attrs) {
    return new Promise(function (resolve) {
      if (!attrs.src && !attrs.href) { resolve(); return; }
      var url = attrs.src || attrs.href;
      if (document.querySelector(tag + '[' + (attrs.src ? 'src' : 'href') + '="' + url + '"]')) {
        resolve();
        return;
      }
      var node = document.createElement(tag);
      Object.keys(attrs).forEach(function (key) { node.setAttribute(key, attrs[key]); });
      node.onload = function () { resolve(); };
      // A stylesheet or script that will not load is not worth hanging the
      // drawer over — it opens either way, plainer than intended.
      node.onerror = function () { resolve(); };
      document.head.appendChild(node);
    });
  }

  function bringTheCart() {
    if (loaded || loading) return Promise.resolve();
    loading = true;
    return Promise.all([
      addOnce('link', { rel: 'stylesheet', href: CART_CSS }),
      addOnce('script', { src: CONFETTI_JS })
    ])
      .then(function () { return addOnce('script', { src: CART_JS }); })
      .then(function () { loaded = true; loading = false; });
  }

  /* ---------- opening and closing ---------- */

  var lastFocus = null;

  function open() {
    if (!drawer.hidden) return bringTheCart();
    lastFocus = document.activeElement;
    drawer.hidden = false;
    // The shop behind must not scroll while this is over it.
    document.documentElement.style.overflow = 'hidden';
    // Reading a layout property makes the browser lay the panel out at
    // translateX(100%) right now, so the class that follows is a change it
    // can animate rather than the state it started in.
    //
    // Deliberately not two animation frames, which is the usual way to do
    // this: requestAnimationFrame does not fire at all in a tab the browser
    // is not painting — another window in front of it is enough. Measured
    // in exactly that state, the drawer unhid itself and then sat off the
    // side of the screen, leaving an invisible sheet over the shop.
    void drawer.offsetWidth;
    drawer.classList.add('is-open');
    var closeButton = drawer.querySelector('[data-sp-drawer-close]');
    if (closeButton) closeButton.focus();
    return bringTheCart();
  }

  function close() {
    if (drawer.hidden) return;
    drawer.classList.remove('is-open');
    document.documentElement.style.overflow = '';
    setTimeout(function () { drawer.hidden = true; }, 280);
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  /* ---------- taking the add ourselves ---------- */

  function formOf(element) {
    return element.closest ? element.closest('form[action*="/cart/add"]') : null;
  }

  function busy(button, yes) {
    if (!button) return;
    button.classList.toggle('loading', yes);
    var spinner = button.querySelector('.loading-overlay__spinner');
    if (spinner) spinner.classList.toggle('hidden', !yes);
    button.disabled = yes;
  }

  function addFrom(form, button) {
    var data = new FormData(form);
    // The theme leaves this input disabled until it submits, and a disabled
    // field is left out of FormData — so the id is read off the element
    // rather than trusted to come along by itself.
    var id = form.querySelector('[name="id"]');
    if (id && id.value) data.set('id', id.value);
    if (!data.get('id')) return;
    if (!data.get('quantity')) data.set('quantity', '1');

    busy(button, true);
    // Opened first, with whatever the cart held a moment ago, so the panel
    // is already on screen while the add is still in the air. Waiting for
    // the server before showing anything is what made the old one feel slow.
    open();

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: data
    })
      .then(function (r) {
        return r.json().then(function (json) {
          if (!r.ok) throw new Error(json.description || json.message || 'Could not add that.');
          return json;
        });
      })
      .then(function () {
        busy(button, false);
        // The cart script may still be downloading. It reads the basket for
        // itself when it starts, so this only matters once it is here.
        document.dispatchEvent(new CustomEvent('sp:cart-refresh'));
        if (window.spConfetti) window.spConfetti({ pieces: 26 });
      })
      .catch(function (err) {
        busy(button, false);
        close();
        var wrapper = form.querySelector('.product-form__error-message-wrapper');
        var message = form.querySelector('.product-form__error-message');
        if (wrapper && message) {
          message.textContent = err.message;
          wrapper.hidden = false;
        } else {
          window.alert(err.message);
        }
      });
  }

  // Capture, at the top of the document, so this runs before the theme's own
  // handler on the form and nothing else gets a chance to send the page
  // somewhere.
  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!form || !form.getAttribute) return;
      if ((form.getAttribute('action') || '').indexOf('/cart/add') === -1) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      addFrom(form, form.querySelector('[name="add"]'));
    },
    true
  );

  // A button that is not of type submit — some themes use a plain button and
  // call submit themselves.
  document.addEventListener(
    'click',
    function (event) {
      if (!event.target || !event.target.closest) return;
      var button = event.target.closest('[name="add"]');
      if (!button || button.type === 'submit') return;
      var form = formOf(button);
      if (!form) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      addFrom(form, button);
    },
    true
  );

  /* ---------- the ways out ---------- */

  drawer.addEventListener('click', function (event) {
    if (event.target.closest && event.target.closest('[data-sp-drawer-close]')) close();
  });

  document.addEventListener('keydown', function (event) {
    if (!drawer.hidden && (event.key === 'Escape' || event.key === 'Esc')) close();
  });

  // Order Now inside the drawer leaves for the checkout; the panel should
  // not still be sliding there when the page changes.
  window.addEventListener('pagehide', close);

  // So anything else on the shop can open it — "view cart" after an add, for
  // one — without knowing how any of this works.
  window.SPDrawer = { open: open, close: close };

  // There WAS a line here stopping every click inside the panel from
  // travelling further, to keep panel clicks off the veil behind it. The
  // cross is inside the panel, and the handler that closes the drawer is on
  // the drawer — so that line was quietly swallowing the one click it most
  // needed to let through. The cross did nothing at all.
  //
  // It was never needed: the veil closes the drawer by carrying
  // data-sp-drawer-close itself, so a click in the panel could not have
  // reached it anyway.
})();
